import Decimal from 'decimal.js';
import {
  processDecimalPart,
  processNumberPart,
  sanitizePercentValue,
  stripPercentSymbol,
} from '@utils/percentageInputsUtils';
import { TableCellVariant } from './types';

export const convertDecimalToPercentage = (
  value: number | string,
  maxDecimals: number = 2,
): string => {
  const isNegative =
    typeof value === 'number' ? value < 0 : value.toString().trim().startsWith('-');

  let absValue: number | string = value;
  if (typeof value === 'number') {
    absValue = Math.abs(value);
    absValue = absValue * Math.pow(10, 2);
    absValue = (
      Math.round(absValue * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals)
    ).toString();
  } else {
    absValue = value.replace(/%/g, '');
    if (isNegative) {
      absValue = absValue.substring(1);
    }
  }

  const sanitizedValue = sanitizePercentValue(absValue.toString());
  let cleanedValue = '';

  if (sanitizedValue.includes('.')) {
    let [numberPart, decimalPart] = sanitizedValue.split('.');
    numberPart = processNumberPart(numberPart);
    decimalPart = processDecimalPart(decimalPart, maxDecimals);
    cleanedValue = `${numberPart}.${decimalPart}`;
  } else {
    cleanedValue = sanitizedValue === '' ? '' : processNumberPart(sanitizedValue);
    cleanedValue = cleanedValue === '' ? '' : `${cleanedValue}`;
  }

  if (isNegative && cleanedValue !== '') {
    cleanedValue = `-${cleanedValue}`;
  }

  return cleanedValue === '' ? '' : `${cleanedValue}%`;
};

/**
 * Converts a percentage value to a decimal value
 * @param value - The percentage value to convert
 * @returns The decimal value
 */
export const convertPercentageToDecimal = (value: string): number => {
  const sanitizedValue = stripPercentSymbol(value);
  const percentageValInStr = new Decimal(sanitizedValue);
  return percentageValInStr.dividedBy(100).toNumber();
};

// New currency utility functions
/**
 * Sanitizes a currency value by removing non-numeric characters except for decimal points, commas and dollar signs
 */
export const sanitizeCurrencyValue = (value: string): string => {
  return value.replace(/[^0-9.,$]/g, '');
};

/**
 * Formats a number with commas for thousands separation, preserving decimal places
 */
export const addCommasToNumber = (value: string): string => {
  // Remove any existing commas first
  const cleanValue = value.replace(/,/g, '');
  if (cleanValue === '' || isNaN(Number(cleanValue))) return '';

  // Split into whole and decimal parts
  const parts = cleanValue.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1];

  // Add commas to the whole part
  const formattedWholePart = Number(wholePart).toLocaleString('en-US');

  // Reconstruct with decimal part if it exists
  return decimalPart !== undefined ? `${formattedWholePart}.${decimalPart}` : formattedWholePart;
};

/**
 * Creates a formatted currency value with $ symbol, comma separation, and decimal support
 */
export const createFormattedCurrencyValue = (value: string): string => {
  // Remove $ symbol before sanitizing
  const valueWithoutDollar = value.replace(/\$/g, '');
  const isNegative = valueWithoutDollar.startsWith('-');
  const sanitizedValue = sanitizeCurrencyValue(valueWithoutDollar);

  if (sanitizedValue === '' || sanitizedValue === ',' || sanitizedValue === '.') return '';

  // Remove commas for processing
  const cleanValue = sanitizedValue.replace(/,/g, '');

  // Handle decimal point validation - allow only one decimal point
  const decimalPoints = (cleanValue.match(/\./g) || []).length;
  if (decimalPoints > 1) return '';

  // Split into whole and decimal parts for processing
  if (cleanValue.includes('.')) {
    let [wholePart, decimalPart] = cleanValue.split('.');

    // Process whole part - remove leading zeros except for single 0
    wholePart = processNumberPart(wholePart);

    // Process decimal part - limit to 2 decimal places
    decimalPart = processDecimalPart(decimalPart, 2);

    const cleanedValue = `${wholePart}.${decimalPart}`;

    // Ensure it's a valid number
    if (isNaN(Number(cleanedValue))) return '';

    // Add commas and dollar sign
    const formattedNumber = addCommasToNumber(cleanedValue);
    return formattedNumber === '' ? '' : `${isNegative ? '-' : ''}$${formattedNumber}`;
  } else {
    // No decimal point - process as whole number
    const processedValue = processNumberPart(cleanValue);

    // Ensure it's a valid number
    if (isNaN(Number(processedValue))) return '';

    // Add commas and dollar sign
    const formattedNumber = addCommasToNumber(processedValue);
    return formattedNumber === '' ? '' : `${isNegative ? '-' : ''}$${formattedNumber}`;
  }
};

/**
 * Handles input change for currency fields with proper cursor positioning
 */
export const handleCurrencyChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  updateState: (formattedValue: string) => void,
): void => {
  const input = e.target;
  const cursorPosition = input.selectionStart || 0;
  const prevValue = input.value;

  // Store whether cursor was at end (excluding $)
  const wasAtEnd = cursorPosition >= prevValue.length;

  // Format the value
  const formattedValue = createFormattedCurrencyValue(prevValue);

  // Determine the length difference between old and new values
  const lengthDiff = formattedValue.length - prevValue.length;

  // Update the state with formatted value
  updateState(formattedValue);

  // Calculate new cursor position
  setTimeout(() => {
    let newPosition;

    // If cursor was at end, keep it at end
    if (wasAtEnd) {
      newPosition = formattedValue.length;
    }
    // If cursor was at the beginning (on $), place it after $
    else if (cursorPosition <= 1 && formattedValue.startsWith('$')) {
      newPosition = 1;
    }
    // Otherwise maintain relative position, adjusting for any length changes
    else {
      newPosition = Math.min(cursorPosition + lengthDiff, formattedValue.length);

      // Ensure cursor is never before the $ symbol
      if (formattedValue.startsWith('$')) {
        newPosition = Math.max(newPosition, 1);
      }
    }

    input.setSelectionRange(newPosition, newPosition);
  }, 0);
};

/**
 * Removes currency symbol and commas, converts to number with decimal support
 */
export const stripCurrencyFormatting = (value: string): number | null => {
  if (value === '' || value === '$' || value === '-$') return null;

  const cleanValue = value.replace(/[$,]/g, '');
  return parseFloat(cleanValue) ?? null;
};

// Legacy currency functions (keeping for backward compatibility)
export const convertDecimalToCurrency = (value: number): string => {
  if (isNaN(value)) return '';
  const isNegative = value < 0;
  return `${isNegative ? '-' : ''}$${Math.abs(value).toLocaleString('en-US')}`;
};

export const convertCurrencyToDecimal = (value: string): number | null => {
  return stripCurrencyFormatting(value);
};

export const formatPercentageValue = (value: string | number): string => {
  if (typeof value === 'number') {
    return value.toFixed(2) + '%';
  }
  return value + '%';
};

export const decimalToPercentage = (value: number | null): string | null => {
  if (value === null) return null;

  return (value * 100).toFixed(2) + '%';
};

/**
 * Returns the styles based on the variant
 * @param variant - The variant to get the styles for
 * @returns The styles based on the variant
 */
export const getStylesBasedOnVariant = (variant: TableCellVariant) => {
  switch (variant) {
    case 'primary':
      return {
        color: '#1B1C17',
        fontWeight: 600,
        backgroundColor: '#2F736E1F',
        fontSize: '14px',
        lineHeight: '20px',
      };
    case 'primary-left':
      return {
        color: '#1B1C17',
        fontWeight: 700,
        backgroundColor: '#2F736E1F',
        fontSize: '12px',
        lineHeight: '20px',
      };
    case 'default':
      return {
        color: '#1B1C17',
        fontWeight: 500,
        backgroundColor: '#FFFFFF',
        fontSize: '14px',
        lineHeight: '20px',
      };
    case 'disabled-text':
      return {
        color: '#B4B4B4',
        fontWeight: 500,
        backgroundColor: '#FFFFFF',
        fontSize: '14px',
        lineHeight: '20px',
      };
    case 'semi-transparent':
      return {
        color: '#6C6C6C',
        fontWeight: 600,
        backgroundColor: '#F0F0F1',
        fontSize: '14px',
        lineHeight: '20px',
      };
    case 'semi-transparent-primary':
      return {
        color: '#1B1C17',
        fontWeight: 600,
        backgroundColor: '#D8DBDD',
        fontSize: '14px',
        lineHeight: '20px',
      };
    case 'semi-transparent-primary-green':
      return {
        color: '#1B1C17',
        fontWeight: 600,
        backgroundColor: '#CDEBE8',
        fontSize: '14px',
        lineHeight: '20px',
      };
    case 'none':
    default:
      return {};
  }
};

// Build border styles conditionally
export const getBorderStyles = (
  isError: boolean,
  isFocused: boolean,
  isLeftEdge: boolean,
  isRightEdge: boolean,
  isBottomEdge: boolean,
  isSelected?: boolean,
  isSelectionTopEdge?: boolean,
  isSelectionBottomEdge?: boolean,
  isSelectionLeftEdge?: boolean,
  isSelectionRightEdge?: boolean,
) => {
  // Determine border styles based on selection edges
  const getTopBorder = () => {
    if (isError) return '1px solid #EF5350';
    if (isFocused) return '2px solid #2F736E';
    if (isSelected && isSelectionTopEdge) return '2px solid #2F736E';
    return '1px solid #2F736E1F';
  };

  const getBottomBorder = () => {
    if (isError) return '1px solid #EF5350';
    if (isFocused) return '2px solid #2F736E';
    if (isSelected && isSelectionBottomEdge) return '2px solid #2F736E';
    if (isBottomEdge) return 'none';
    return '1px solid #2F736E1F';
  };

  const getLeftBorder = () => {
    if (isError) return '1px solid #EF5350';
    if (isFocused) return '2px solid #2F736E';
    if (isSelected && isSelectionLeftEdge) return '2px solid #2F736E';
    if (isLeftEdge) return 'none';
    return '1px solid #2F736E1F';
  };

  const getRightBorder = () => {
    if (isError) return '1px solid #EF5350';
    if (isFocused) return '2px solid #2F736E';
    if (isSelected && isSelectionRightEdge) return '2px solid #2F736E';
    if (isRightEdge) return 'none';
    return '1px solid #2F736E1F';
  };

  return {
    borderTop: getTopBorder(),
    borderLeft: getLeftBorder(),
    borderRight: getRightBorder(),
    borderBottom: getBottomBorder(),
  };
};
